// Lightweight episode-page prefetch. Kicked off on hover / pointerdown of
// episode title links so navigation feels instant — EpisodePage reads from
// this cache before falling back to a fresh fetch.
import { supabase } from "@/integrations/supabase/client";

const EP_SELECT =
  "id, title, slug, description, meta_title, meta_description, audio_url, duration_seconds, published_at, spotify_episode_url, apple_episode_url, content_source, podcast:podcasts(id, name, slug, artwork_url, description, spotify_url, apple_podcasts_url, youtube_channel_url, website_url)";

export interface PrefetchedEpisode {
  episode: any;
  insights: any | null;
  topicLinks: { topic_slug: string }[] | null;
  fetchedAt: number;
}

const TTL_MS = 60_000;
const cache = new Map<string, Promise<PrefetchedEpisode | null>>();

async function fetchEpisode(slug: string): Promise<PrefetchedEpisode | null> {
  const { data: ep, error } = await supabase
    .from("episodes")
    .select(EP_SELECT)
    .eq("slug", slug)
    .maybeSingle();
  if (error || !ep) return null;

  const [{ data: insights }, { data: topicLinks }] = await Promise.all([
    supabase
      .from("episode_insights")
      .select("ai_summary, takeaways, themes, topics, guest_name, host_name, star_rating")
      .eq("episode_id", (ep as any).id)
      .maybeSingle(),
    supabase
      .from("episode_topics")
      .select("topic_slug")
      .eq("episode_id", (ep as any).id),
  ]);

  return { episode: ep, insights: insights ?? null, topicLinks: topicLinks ?? null, fetchedAt: Date.now() };
}

export function prefetchEpisode(slug: string | null | undefined) {
  if (!slug) return;
  const existing = cache.get(slug);
  if (existing) return;
  const p = fetchEpisode(slug).catch(() => null);
  cache.set(slug, p);
  // Drop after TTL so we don't serve stale data forever.
  setTimeout(() => {
    const cur = cache.get(slug);
    if (cur === p) cache.delete(slug);
  }, TTL_MS);
}

export function consumePrefetchedEpisode(slug: string | null | undefined) {
  if (!slug) return null;
  const p = cache.get(slug);
  if (!p) return null;
  cache.delete(slug);
  return p;
}
